import { useQuery } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { stats } from '@/lib/api'

type SortMode = 'playtime' | 'recent' | 'alpha' | 'gamerscore'
type PlatformFilter = 'all' | 'steam' | 'xbox'

interface UnifiedGame {
  id: string
  name: string
  platform: 'steam' | 'xbox'
  playtimeHours: number
  lastPlayed: Date
  imageUrl?: string
  gamerscore?: number
  maxGamerscore?: number
  detail: string
  platformColor: string
}

export function GameLibrary() {
  const [sort, setSort] = useState<SortMode>('playtime')
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>('all')
  const [search, setSearch] = useState('')
  const [showCount, setShowCount] = useState(48)

  const { data, isLoading } = useQuery({
    queryKey: ['stats'],
    queryFn: async () => {
      const result = await stats.getAll()
      return result.data
    },
  })

  const allGames = useMemo(() => {
    const games: UnifiedGame[] = []

    if (data?.steam?.games) {
      for (const game of data.steam.games) {
        if (game.playtimeForever <= 0) continue
        games.push({
          id: `steam-${game.appId}`,
          name: game.name,
          platform: 'steam',
          playtimeHours: Math.round(game.playtimeForever / 60),
          lastPlayed: new Date(),
          imageUrl: `https://cdn.cloudflare.steamstatic.com/steam/apps/${game.appId}/header.jpg`,
          detail: `${Math.round(game.playtimeForever / 60)}h`,
          platformColor: '#66c0f4',
        })
      }
    }

    if (data?.xbox?.recentGames) {
      for (const game of data.xbox.recentGames) {
        games.push({
          id: `xbox-${game.titleId}`,
          name: game.name,
          platform: 'xbox',
          playtimeHours: 0,
          lastPlayed: new Date(game.lastPlayed || Date.now()),
          imageUrl: game.imageUrl,
          gamerscore: game.gamerscore,
          maxGamerscore: game.maxGamerscore,
          detail: `${game.gamerscore}/${game.maxGamerscore}G`,
          platformColor: '#107c10',
        })
      }
    }

    return games
  }, [data])

  const filteredGames = useMemo(() => {
    let list = allGames

    if (platformFilter !== 'all') {
      list = list.filter((g) => g.platform === platformFilter)
    }

    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter((g) => g.name.toLowerCase().includes(q))
    }

    list.sort((a, b) => {
      switch (sort) {
        case 'playtime': return b.playtimeHours - a.playtimeHours
        case 'recent': return b.lastPlayed.getTime() - a.lastPlayed.getTime()
        case 'alpha': return a.name.localeCompare(b.name)
        case 'gamerscore': return (b.gamerscore || 0) - (a.gamerscore || 0)
        default: return 0
      }
    })

    return list
  }, [allGames, platformFilter, search, sort])

  const steamCount = allGames.filter((g) => g.platform === 'steam').length
  const xboxCount = allGames.filter((g) => g.platform === 'xbox').length

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="h-10 w-48 bg-gray-800 rounded animate-pulse mb-4" />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="rounded-xl bg-gray-800 animate-pulse aspect-[460/215]" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-display font-bold mb-1">Game Library</h1>
        <p className="text-gray-400">
          {allGames.length} games across {steamCount > 0 && xboxCount > 0 ? '2 platforms' : '1 platform'}
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-6 bg-gray-900/50 rounded-xl p-3 border border-gray-800">
        {/* Sort */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500 uppercase tracking-wider mr-1">Sort</span>
          {([
            ['playtime', 'Playtime'],
            ['recent', 'Recent'],
            ['alpha', 'A-Z'],
            ['gamerscore', 'Gamerscore'],
          ] as [SortMode, string][]).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setSort(key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                sort === key
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="w-px h-6 bg-gray-700 hidden sm:block" />

        {/* Platform Filter */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500 uppercase tracking-wider mr-1">Platform</span>
          {([
            ['all', `All (${allGames.length})`],
            ['steam', `Steam (${steamCount})`],
            ['xbox', `Xbox (${xboxCount})`],
          ] as [PlatformFilter, string][]).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setPlatformFilter(key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                platformFilter === key
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="w-px h-6 bg-gray-700 hidden sm:block" />

        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search games..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-primary-500 transition-colors"
          />
        </div>
      </div>

      {/* Results */}
      {filteredGames.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-5xl mb-4">🔍</div>
          <p className="text-gray-400">No games found</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {filteredGames.slice(0, showCount).map((game) => (
              <LibraryGameCard key={game.id} game={game} />
            ))}
          </div>

          {filteredGames.length > showCount && (
            <div className="text-center mt-8">
              <button
                onClick={() => setShowCount((c) => c + 48)}
                className="px-6 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors border border-gray-700"
              >
                Load more ({filteredGames.length - showCount} remaining)
              </button>
            </div>
          )}

          <p className="text-center text-gray-600 text-sm mt-4">
            Showing {Math.min(showCount, filteredGames.length)} of {filteredGames.length} games
          </p>
        </>
      )}
    </div>
  )
}

function LibraryGameCard({ game }: { game: UnifiedGame }) {
  const completion =
    game.maxGamerscore && game.maxGamerscore > 0
      ? Math.round((game.gamerscore! / game.maxGamerscore) * 100)
      : null

  return (
    <div className="group relative overflow-hidden rounded-xl bg-gray-900 border border-gray-800 hover:border-gray-600 transition-all hover:-translate-y-1">
      {/* Image */}
      <div className="relative aspect-[460/215] overflow-hidden bg-gray-800">
        {game.imageUrl && (
          <img
            src={game.imageUrl}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

        {/* Platform Badge */}
        <span
          className="absolute top-1.5 right-1.5 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded"
          style={{ backgroundColor: `${game.platformColor}30`, color: game.platformColor }}
        >
          {game.platform}
        </span>

        {/* Completion Badge */}
        {completion !== null && (
          <span
            className={`absolute top-1.5 left-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded ${
              completion === 100
                ? 'bg-green-500 text-black'
                : completion >= 50
                  ? 'bg-yellow-500/80 text-black'
                  : 'bg-gray-700/80 text-white'
            }`}
          >
            {completion}%
          </span>
        )}
      </div>

      {/* Info */}
      <div className="p-2.5">
        <h3 className="text-xs font-semibold truncate mb-1 group-hover:text-primary-400 transition-colors">
          {game.name}
        </h3>
        <div className="flex justify-between items-center">
          <span className="text-[10px] text-gray-500">{game.detail}</span>
          {game.playtimeHours > 0 && (
            <span className="text-[10px] text-gray-600">{game.playtimeHours}h</span>
          )}
        </div>
      </div>
    </div>
  )
}
